import {
  type MutableRefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

type ModifierKey = "altKey" | "ctrlKey" | "metaKey" | "shiftKey";
// Shortcuts that are used with modifier keys need to be mapped here
const CODE_TO_KEY_MAP: { [code: string]: string } = {
  Digit1: "1",
  Digit2: "2",
  Digit3: "3",
  Digit4: "4",
  Digit5: "5",
  Digit6: "6",
  Digit7: "7",
  Digit8: "8",
};

// Checks if modifier key (alt, ctrl, meta, shift) is the ONLY modifier key pressed
function isModifierKeyPressed(event: KeyboardEvent, key: ModifierKey) {
  const otherKeys = ["altKey", "ctrlKey", "metaKey", "shiftKey"].filter(
    (k) => k !== key,
  ) as ModifierKey[];
  return event[key] && !otherKeys.some((k) => event[k]);
}

function isNoModifierKeyPressed(event: KeyboardEvent) {
  const keys: ModifierKey[] = ["altKey", "ctrlKey", "metaKey", "shiftKey"];
  return !keys.some((k) => event[k]);
}

// Check if the active element is in an editable context (input, textarea, contenteditable, or within a field)
function isInEditableContext(): boolean {
  const activeElement = document.activeElement;
  if (!activeElement) {
    return false;
  }

  // Check if it's a standard input or textarea
  if (activeElement.nodeName === "INPUT" || activeElement.nodeName === "TEXTAREA") {
    return true;
  }

  // Check if it's a contenteditable element
  if (
    activeElement.hasAttribute("contenteditable") &&
    activeElement.getAttribute("contenteditable") !== "false"
  ) {
    return true;
  }

  // Check if it's within a contenteditable element
  let parent = activeElement.parentElement;
  while (parent) {
    if (
      parent.hasAttribute("contenteditable") &&
      parent.getAttribute("contenteditable") !== "false"
    ) {
      return true;
    }
    parent = parent.parentElement;
  }

  // Check if it's within a field container (fields have class names like "field-RichTextField", "field-CodeField", etc.)
  const fieldContainer = activeElement.closest('[class*="field-"]');
  if (fieldContainer) {
    return true;
  }

  // Check for tldraw element (may be on active element or any parent)
  // When drawing on a whiteboard, the active element might be a canvas/child element
  // within the tldraw container that has the data-tldraw attribute
  const tldrawContainer = activeElement.closest("[data-tldraw]");
  if (tldrawContainer) {
    return true;
  }

  return false;
}

// https://devtrium.com/posts/how-keyboard-shortcut
// NOTE: use of the ctrl and meta modifier keys can be implemented in the future if needed
type UseKeyPressOptions = {
  keys?: string[];
  altKeys?: string[];
  shiftKeys?: string[];
  callback?: (key: string) => void;
  altCallback?: (key: string) => void;
  shiftCallback?: (key: string) => void;
  node?: HTMLElement | null;
};

const noop = () => {};

const _handleKeyDown = (
  evt: KeyboardEvent,
  _keys: string[],
  _callBackRef: MutableRefObject<(key: string) => void>,
) => {
  const normalizedKey = evt.key.length === 1 ? evt.key.toLowerCase() : evt.key;
  const mappedKey = CODE_TO_KEY_MAP[evt.code];

  if (_keys.includes(normalizedKey)) {
    _callBackRef.current(normalizedKey);
  } else if (_keys.includes(mappedKey)) {
    _callBackRef.current(mappedKey);
  }
};

const useKeyPress = ({
  keys = [],
  altKeys = [],
  shiftKeys = [],
  callback = noop,
  altCallback = noop,
  shiftCallback = noop,
  node = null,
}: UseKeyPressOptions = {}) => {
  // Implement the callback ref pattern
  const callbackRef = useRef(callback);
  const altCallbackRef = useRef(altCallback);
  const shiftCallbackRef = useRef(shiftCallback);
  useLayoutEffect(() => {
    callbackRef.current = callback;
    altCallbackRef.current = altCallback;
    shiftCallbackRef.current = shiftCallback;
  });

  // Handle what happens on key press
  const handleKeyDown = useCallback((event: Event) => {
    // Do not handle key press when in an editable context (input, textarea, contenteditable, or field)
    if (isInEditableContext()) {
      return;
    }
    // Check if the key is part of the ones we want
    const evt = event as KeyboardEvent;
    if (isNoModifierKeyPressed(evt)) {
      _handleKeyDown(evt, keys, callbackRef);
    } else if (isModifierKeyPressed(evt, "altKey")) {
      _handleKeyDown(evt, altKeys, altCallbackRef);
    } else if (isModifierKeyPressed(evt, "shiftKey")) {
      _handleKeyDown(evt, shiftKeys, shiftCallbackRef);
    }
    // keys, altKeys, shiftKeys are effectively static
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (keys.length === 0 && altKeys.length === 0 && shiftKeys.length === 0) {
      return;
    }

    // Target is either the provided node or the document
    const targetNode = node ?? document;
    // Attach the event listener
    targetNode.addEventListener("keydown", handleKeyDown);

    // Remove the event listener
    return () => targetNode.removeEventListener("keydown", handleKeyDown);
  }, [altKeys.length, handleKeyDown, keys.length, node, shiftKeys.length]);
};

const useKeyHold = (
  keys: string[],
  keyDownCallback: (event: Event) => void,
  keyUpCallback: (event: Event) => void,
  node: HTMLElement | null = null,
) => {
  // Implement the callback ref pattern
  const keyDownCallbackRef = useRef(keyDownCallback);
  const keyUpCallbackRef = useRef(keyUpCallback);
  useLayoutEffect(() => {
    keyDownCallbackRef.current = keyDownCallback;
    keyUpCallbackRef.current = keyUpCallback;
  });
  const [isHoldActive, setIsHoldActive] = useState(false);

  // Handle what happens on key down
  const handleKeyDown = useCallback(
    (event: Event) => {
      if (isInEditableContext()) {
        return;
      }

      if ((event as KeyboardEvent).repeat) return;
      // Check if one of the key is part of the ones we want
      if (keys.some((key) => (event as KeyboardEvent).key === key)) {
        keyDownCallbackRef.current(event);
        setIsHoldActive(true);
      }
    },
    [keys],
  );

  // Handle what happens on key up
  const handleKeyUp = useCallback(
    (event: Event) => {
      if (isInEditableContext()) {
        return;
      }

      // Check if one of the key is part of the ones we want
      if (keys.some((key) => (event as KeyboardEvent).key === key)) {
        keyUpCallbackRef.current(event);
        setIsHoldActive(false);
      }
    },
    [keys],
  );

  // Trigger the keyup event if the user switches tabs while holding the key down
  const handleVisibilityChange = useCallback(() => {
    if (isHoldActive && document.visibilityState === "hidden") {
      keys.forEach((key) => {
        const event = new KeyboardEvent("keyup", { key });
        keyUpCallbackRef.current(event);
      });
      setIsHoldActive(false);
    }
  }, [isHoldActive, keys]);

  // Trigger the keyup event if the user clicks outside the window while holding the key down
  const handleBlurEvent = useCallback(() => {
    if (isHoldActive) {
      keys.forEach((key) => {
        const event = new KeyboardEvent("keyup", { key });
        keyUpCallbackRef.current(event);
      });
      setIsHoldActive(false);
    }
  }, [isHoldActive, keys]);

  // Trigger the keyup event if the user clicks elsewhere in the classroom while holding the key down
  const handleMouseDown = useCallback(() => {
    if (isHoldActive) {
      keys.forEach((key) => {
        const event = new KeyboardEvent("keyup", { key });
        keyUpCallbackRef.current(event);
      });
      setIsHoldActive(false);
    }
  }, [isHoldActive, keys]);

  useEffect(() => {
    // Target is either the provided node or the document
    const targetNode = node ?? document;
    // Attach the event listener
    targetNode.addEventListener("keydown", handleKeyDown);
    targetNode.addEventListener("keyup", handleKeyUp);
    targetNode.addEventListener("mousedown", handleMouseDown);
    targetNode.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlurEvent);

    // Remove the event listener
    return () => {
      targetNode.removeEventListener("keydown", handleKeyDown);
      targetNode.removeEventListener("keyup", handleKeyUp);
      targetNode.removeEventListener("mousedown", handleMouseDown);
      targetNode.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlurEvent);
    };
  }, [handleBlurEvent, handleKeyDown, handleKeyUp, handleMouseDown, handleVisibilityChange, node]);
};

export { useKeyHold, useKeyPress };
